import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import useEscapeKey from "@hooks/useEscapeKey";

const ScanningBlockModal = ({
  isOpen,
  onClose,
  type = "refresh", // "refresh" or "navigation"
  loading = false,
  "data-testid": dataTestId = "scanning-block-modal",
}) => {
  const fieldValueTestId = (name) => `${dataTestId}-field-${name}-value`;
  const { colors } = useTheme();
  const { t } = useTranslation();

  useEscapeKey(onClose, isOpen && !loading);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  // Get title and description based on type
  const getModalContent = () => {
    if (type === "navigation") {
      return {
        title: t("project.scanning-in-progress") || "Scanning in Progress",
        description: t("project.cannot-leave-during-scan") || "Cannot leave page during scanning process. Please wait for the scan to complete.",
      };
    } else {
      return {
        title: t("project.scanning-in-progress") || "Scanning in Progress", 
        description: t("project.cannot-refresh-during-scan") || "Cannot refresh page during scanning process. Please wait for the scan to complete.",
      };
    }
  };

  const { title, description } = getModalContent();

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 z-[10000] flex items-center justify-start sm:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto`}
    >
      <div
        data-testid={dataTestId}
        className={`relative w-full max-w-md min-w-md ${colors.bgModal} transform overflow-hidden rounded-lg shadow-xl transition-all duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Content */}
        <div className="p-6" data-testid={`${dataTestId}-content`}>
          {/* Icon and Title */}
          <div className="mb-4 flex items-center justify-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center">
              <svg className={`h-14 w-14 ${colors.textWarning}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <div className="mb-4 text-center" data-testid={`${dataTestId}-header`}>
            <h3 className={`text-lg font-semibold ${colors.textPrimary} mb-2 uppercase`} data-testid={`${dataTestId}-title`}>
              {title}
            </h3>

            {/* Description */}
            <p className={`text-sm ${colors.textSecondary} whitespace-pre-line`} data-testid={fieldValueTestId("message")}>
              {description}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-center" data-testid={`${dataTestId}-footer`}>
            {/* Cancel Button Only */}
            <button
              type="button"
              data-testid={`${dataTestId}-cancel-button`}
              onClick={handleCancel}
              disabled={loading}
              className={`rounded-md border px-6 py-2 text-sm font-medium transition-colors ${colors.borderOutLineButton} ${colors.textActive} hover:${colors.bgPrimary} min-w-[120px] disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {loading ? (
                <svg
                  className="mr-2 -ml-1 h-4 w-4 animate-spin text-current inline"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : null}
              {loading ? t("common.loading") || "Loading..." : t("common.cancel-button") || "Cancel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScanningBlockModal;